/**
 * Expands and collapses truncated text with a height animation.
 */
class TextReadMore extends HTMLElement {
  #expanded = false;
  #collapsedHeight = 0;
  #animation = null;
  #animating = false;
  #animationSpeed = 300;

  connectedCallback() {
    if (this.dataset.initialized === 'true') return;

    const buttons = this.querySelectorAll('.text-read-more__button');
    if (!buttons.length) return;

    this.dataset.initialized = 'true';
    buttons.forEach((button) => button.addEventListener('click', this.#onToggle));
  }

  disconnectedCallback() {
    this.#animation?.cancel();
  }

  #onToggle = (event) => {
    event.preventDefault();
    if (this.#animating) return;

    if (this.#expanded) {
      this.#collapse();
    } else {
      this.#expand();
    }
  };

  #getViews() {
    return {
      content: this.querySelector('.text-read-more__content'),
      collapsed: this.querySelector('.text-read-more__collapsed'),
      expanded: this.querySelector('.text-read-more__expanded'),
      inlineButton: this.querySelector('.text-read-more__button--inline'),
      trailingButton: this.querySelector('.text-read-more__button--trailing'),
    };
  }

  #expand() {
    const { content, collapsed, expanded, inlineButton, trailingButton } = this.#getViews();
    if (!content || !collapsed || !expanded || !inlineButton || !trailingButton) return;

    const startHeight = content.offsetHeight;
    this.#collapsedHeight = startHeight;

    this.#expanded = true;
    this.#setExpandedState(true, inlineButton, trailingButton);

    collapsed.hidden = true;
    expanded.hidden = false;
    trailingButton.hidden = false;

    const endHeight = content.scrollHeight;

    this.#animateHeight(content, startHeight, endHeight, () => {});
  }

  #collapse() {
    const { content, collapsed, expanded, inlineButton, trailingButton } = this.#getViews();
    if (!content || !collapsed || !expanded || !inlineButton || !trailingButton) return;

    const startHeight = content.offsetHeight;

    this.#expanded = false;
    this.#setExpandedState(false, inlineButton, trailingButton);

    expanded.hidden = true;
    trailingButton.hidden = true;
    collapsed.hidden = false;

    const endHeight = this.#collapsedHeight || content.scrollHeight;

    this.#setExpandedState(false, inlineButton, trailingButton);
    this.#animateHeight(content, startHeight, endHeight, () => {});
  }

  #setExpandedState(expanded, inlineButton, trailingButton) {
    this.dataset.expanded = expanded ? 'true' : 'false';
    inlineButton.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    trailingButton.setAttribute('aria-expanded', expanded ? 'true' : 'false');
  }

  #animateHeight(content, startHeight, endHeight, onFinish) {
    content.style.overflow = 'hidden';
    this.#animation?.cancel();
    this.#animating = true;

    this.#animation = content.animate(
      { height: [`${startHeight}px`, `${endHeight}px`] },
      { duration: this.#animationSpeed, easing: 'ease-in-out' }
    );

    this.#animation.onfinish = () => {
      content.style.removeProperty('overflow');
      content.style.removeProperty('height');
      this.#animating = false;
      onFinish();
    };
  }
}

if (!customElements.get('text-read-more')) {
  customElements.define('text-read-more', TextReadMore);
}
